/**
 * A notifier is somewhere Fleece can send a message: a Discord channel's webhook today.
 * It knows nothing about what the message is for, so the position monitor is one sender
 * among those still to come. The shape follows mini-cloud's, so the two consoles read
 * alike.
 */

export type NotifierType = 'discord';
export const NOTIFIER_TYPES: ReadonlyArray<NotifierType> = ['discord'];

export const NOTIFIER_LIMITS = {
  nameLength: 100,
  descriptionLength: 1024,
  channelLength: 100,
} as const;

/** A Discord channel as written: the webhook URL is the credential, so it is only ever sent, never shown back. */
export interface DiscordTargetInput {
  readonly type: 'discord';
  /** The channel's name, as the operator calls it. The webhook URL only carries its id. */
  readonly channel: string;
  /** Required when the notifier is created; left out of an update, the stored one is kept. */
  readonly webhookUrl?: string;
}

export type NotifierTargetInput = DiscordTargetInput;

/** A Discord channel as shown: the webhook's id, which tells two webhooks apart without being able to post to either. */
export interface DiscordTarget {
  readonly type: 'discord';
  readonly channel: string;
  readonly webhookId: string;
}

export type NotifierTarget = DiscordTarget;

export interface Notifier {
  /** Assigned by the service, and what everything that sends to the notifier refers to it by. */
  readonly notifierId: string;
  /** Unique, and free to change. The position monitor sends to `monitor` and `attention` unless told otherwise. */
  readonly name: string;
  readonly description?: string;
  readonly target: NotifierTarget;
  /** Bumped on every save; an update must name the one it was made from. */
  readonly version: number;
  readonly createdAt: number;
  readonly updatedAt: number;
}

/** What it takes to post: held only by what posts. */
export interface DiscordNotifierSecret {
  readonly type: 'discord';
  readonly webhookUrl: string;
}

export type NotifierSecret = DiscordNotifierSecret;

// Discord serves webhooks from its canary and PTB hosts and the old discordapp.com
// domain too, and a URL copied from any of them works the same.
const DISCORD_WEBHOOK_URL = /^https:\/\/(?:(?:canary|ptb)\.)?discord(?:app)?\.com\/api(?:\/v\d+)?\/webhooks\/(\d+)\/([A-Za-z0-9_-]+)\/?$/;

/** The webhook's id, or `undefined` when the URL is not a Discord webhook URL. */
export function discordWebhookId(url: string): string | undefined {
  return DISCORD_WEBHOOK_URL.exec(url.trim())?.[1];
}
